/**
 * PATH: packages/job-hub/src/jobs/tabs/JobQaListChips.tsx
 * FILE_ID: JOBHUB-JOBS-QA-LIST-CHIPS-001
 * PURPOSE: Selection leaves for the job QA tab — inspection chips (rename /
 *          confirm-delete overlays) and the areas-rail row (inline rename).
 * WHY:     Extracted VERBATIM from JobQaTab.tsx (1,127 lines) to bring the
 *          frame under the 500-line ceiling. Do not re-author, transplant.
 * LANDMINE: InspectionChip's rename/delete overlays are absolutely positioned
 *          over the chip body — the chip button stays mounted underneath, so
 *          onClick must not fire through the overlay.
 * RELATED:
 * - JOBHUB-JOBS-TAB-QA-001 (the frame that renders these)
 */
import { useEffect, useRef, useState, type MouseEvent, type TouchEvent } from 'react'
import { ChevronRight, Copy, ExternalLink, Loader2, Pencil, Settings, Trash2 } from 'lucide-react'
import { cn } from '@quotech/tenant-shared/lib/utils'
import { InlineRenameInput } from '../qa/InlineRenameInput'
import { QaDragHandle } from '../qa/QaDragHandle'
import { qaHubRecordUrl } from '../qa/qaCrossDoorLinks'
import { qaInspectionStatusLabel } from '../qa/qaInspectionStatusLabel'
import type { QaArea, QaInspectionSummary } from '../qa/qaApi'

type MenuPoint = { clientX: number; clientY: number }

// Reused from: AreaRow long-press (500 ms timer); useLongPress skips <button> targets, which fill this chip.
function useHoldToOpenMenu(onOpen: ((point: MenuPoint) => void) | undefined) {
  const hold = useRef<number | null>(null)
  const suppressUntil = useRef(0)
  function armMenu(point: MenuPoint) {
    if (!onOpen) return
    if (hold.current) window.clearTimeout(hold.current)
    hold.current = window.setTimeout(() => {
      suppressUntil.current = Date.now() + 750
      onOpen(point)
    }, 500)
  }
  function disarmMenu() {
    if (hold.current) window.clearTimeout(hold.current)
    hold.current = null
  }
  return {
    onTouchStart: onOpen ? (event: TouchEvent) => {
      const touch = event.touches[0]
      if (touch) armMenu({ clientX: touch.clientX, clientY: touch.clientY })
    } : undefined,
    onTouchEnd: disarmMenu,
    onTouchMove: disarmMenu,
    onClickCapture: (event: MouseEvent) => {
      if (Date.now() > suppressUntil.current) return
      event.preventDefault()
      event.stopPropagation()
      suppressUntil.current = 0
    },
  }
}

export function AreaRow({
  area,
  active,
  renaming,
  onSelect,
  onRename,
  selection,
  collapsible = false,
  startRename = false,
  onRenameStarted,
  onOpenMenu,
  reorder: _reorder,
}: {
  area: QaArea
  active: boolean
  renaming: boolean
  onSelect: () => void
  onRename: (name: string) => void
  selection: { selected: boolean; onToggle: () => void }
  /** Phone accordion: the chevron must reflect open/closed. */
  collapsible?: boolean
  startRename?: boolean
  onRenameStarted?: () => void
  onOpenMenu?: (event: { clientX: number; clientY: number }) => void
  reorder?: {
    canMoveUp: boolean
    canMoveDown: boolean
    busy?: boolean
    onMoveUp: () => void
    onMoveDown: () => void
  }
}) {
  const [editing, setEditing] = useState(false)
  const press = useHoldToOpenMenu(onOpenMenu)
  useEffect(() => {
    if (!startRename) return
    setEditing(true)
    onRenameStarted?.()
  }, [startRename, onRenameStarted])

  if (editing) {
    return (
      <li>
        <InlineRenameInput
          initialValue={area.area_name}
          busy={renaming}
          ariaLabel="Rename area"
          className="px-1 py-1"
          onSubmit={(name) => {
            onRename(name)
            setEditing(false)
          }}
          onCancel={() => setEditing(false)}
        />
      </li>
    )
  }

  return (
    <li
      data-qa-area-row={area.id}
      data-qa-area-name={area.area_name}
      data-qa-drop={`location:${area.id}`}
      className="flex items-center data-[qa-drop-active=true]:border-t-2 data-[qa-drop-active=true]:border-primary"
      onContextMenu={onOpenMenu ? (event) => {
        event.preventDefault()
        onOpenMenu({ clientX: event.clientX, clientY: event.clientY })
      } : undefined}
      {...press}
    >
      <input
        type="checkbox"
        checked={selection.selected}
        onChange={selection.onToggle}
        onClick={(event) => event.stopPropagation()}
        aria-label={`Select ${area.area_name}`}
        data-testid={`qa-area-select-${area.id}`}
        className="mr-1.5 h-3.5 w-3.5 shrink-0 rounded border-border"
      />
      <QaDragHandle kind="location" id={area.id} label={area.area_name} />
      <button
        type="button"
        title={active ? 'Click again to rename' : undefined}
        onClick={active ? () => setEditing(true) : onSelect}
        onDoubleClick={() => setEditing(true)}
        className={cn(
          'min-w-0 flex-1 flex items-center justify-between gap-2 px-2 py-2 rounded-lg text-left text-sm transition-colors',
          active ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-accent',
        )}
      >
        <span className="min-w-0 flex-1">
          <span className="block truncate" title={area.area_name}>
            {area.area_name}
          </span>
          {(area.product_names?.length ?? 0) > 0 || area.assigned_to_name || area.due_date ? (
            <span
              className="block truncate text-[11px] text-muted-foreground"
              title={[area.product_names?.join(', '), area.assigned_to_name, area.due_date].filter(Boolean).join(' · ')}
            >
              {[
                area.product_names?.join(', '),
                area.assigned_to_name,
                area.due_date,
              ].filter(Boolean).join(' · ')}
            </span>
          ) : null}
        </span>
        {collapsible && (
          <ChevronRight
            className={cn('h-3.5 w-3.5 shrink-0 opacity-50 transition-transform', active && 'rotate-90')}
            aria-hidden
          />
        )}
      </button>
    </li>
  )
}

export function InspectionChip({
  inspection,
  active,
  compact = false,
  confirming,
  deleting,
  renaming,
  onClick,
  onRename,
  onRequestDelete,
  onCancelDelete,
  onConfirmDelete,
  onOpenSettings,
  onDuplicate,
  onOpenMenu,
  renameRequested = false,
  onRenameStarted,
}: {
  inspection: QaInspectionSummary
  active: boolean
  compact?: boolean
  confirming: boolean
  deleting: boolean
  renaming: boolean
  onClick: () => void
  onRename: (name: string) => void
  onRequestDelete: () => void
  onCancelDelete: () => void
  onConfirmDelete: () => void
  onOpenSettings?: () => void
  onDuplicate?: (inspection: QaInspectionSummary) => void
  onOpenMenu?: (inspection: QaInspectionSummary, x: number, y: number) => void
  renameRequested?: boolean
  onRenameStarted?: () => void
}) {
  const [editing, setEditing] = useState(false)
  const press = useHoldToOpenMenu(onOpenMenu ? (point) => onOpenMenu(inspection, point.clientX, point.clientY) : undefined)
  const pct = inspection.checklist_completion_percent ?? 0
  useEffect(() => {
    if (!renameRequested) return
    setEditing(true)
    onRenameStarted?.()
  }, [renameRequested, onRenameStarted])
  return (
    <div
      className={cn(
        'group relative rounded-xl border transition-colors',
        compact ? 'flex h-11 min-w-[180px] items-center' : 'min-w-[200px]',
        active ? 'border-primary bg-primary/15' : 'border-border bg-card hover:bg-accent',
        'flex items-center',
      )}
      onContextMenu={onOpenMenu ? (event) => {
        event.preventDefault()
        onOpenMenu(inspection, event.clientX, event.clientY)
      } : undefined}
      {...press}
    >
      <button
        type="button"
        aria-label="Settings (records)"
        className="inline-flex h-11 w-11 shrink-0 items-center justify-center text-muted-foreground hover:text-foreground"
        onClick={(event) => {
          event.stopPropagation()
          onOpenSettings?.()
        }}
        onPointerDown={(event) => event.stopPropagation()}
      >
        <Settings className="h-4 w-4" aria-hidden />
      </button>
      <button
        type="button"
        onClick={onClick}
        className={cn('block w-full min-w-0 text-left', compact ? 'px-2.5' : 'px-3 py-2')}
      >
        {compact ? (
          <span className="flex min-w-0 items-center gap-2">
            <span
              className="min-w-0 truncate text-[13px] font-medium text-foreground"
              title={inspection.inspection_name}
            >
              {inspection.inspection_name}
            </span>
            <span className="shrink-0 rounded bg-secondary px-1.5 py-0.5 text-[10px] capitalize text-muted-foreground">
              {qaInspectionStatusLabel(inspection.status)}
            </span>
            <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">{pct}%</span>
          </span>
        ) : (
          <>
            <p
              className="max-w-[220px] truncate text-sm font-medium text-foreground"
              title={inspection.inspection_name}
            >
              {inspection.inspection_name}
            </p>
            <div className="mt-1.5 flex items-center gap-2">
              <span className="rounded bg-secondary px-1.5 py-0.5 text-[10px] capitalize text-muted-foreground">
                {qaInspectionStatusLabel(inspection.status)}
              </span>
              <span className="text-[11px] tabular-nums text-muted-foreground">{pct}%</span>
            </div>
          </>
        )}
      </button>

      {editing ? (
        <div className="absolute inset-0 flex items-center rounded-xl bg-card/95 px-2">
          <InlineRenameInput
            initialValue={inspection.inspection_name}
            busy={renaming}
            ariaLabel="Rename QA list"
            className="w-full"
            onSubmit={(name) => {
              onRename(name)
              setEditing(false)
            }}
            onCancel={() => setEditing(false)}
          />
        </div>
      ) : confirming ? (
        <div className="absolute inset-0 flex items-center justify-center gap-1.5 rounded-xl bg-card/95 px-2">
          <span className="text-[11px] text-muted-foreground">Delete?</span>
          <button
            type="button"
            onClick={onConfirmDelete}
            disabled={deleting}
            className="inline-flex items-center gap-1 rounded-md bg-destructive px-2 py-1 text-[11px] font-medium text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50"
          >
            {deleting ? (
              <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
            ) : (
              <Trash2 className="h-3 w-3" aria-hidden />
            )}
            Yes
          </button>
          <button
            type="button"
            onClick={onCancelDelete}
            className="rounded-md border border-border px-2 py-1 text-[11px] text-muted-foreground hover:text-foreground"
          >
            No
          </button>
        </div>
      ) : (
        <div className={cn(
          'absolute inset-y-0 right-0 flex items-center gap-0.5 rounded-r-xl px-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity',
          active ? 'bg-card' : 'bg-card group-hover:bg-accent',
        )}>
          <a
            href={qaHubRecordUrl(inspection.id)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(event) => event.stopPropagation()}
            className="rounded-md p-1 text-muted-foreground hover:text-primary hover:bg-background transition-colors"
            title="Open in QA hub"
            aria-label={`Open ${inspection.inspection_name} in the QA hub`}
          >
            <ExternalLink className="h-3.5 w-3.5" aria-hidden />
          </a>
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="rounded-md p-1 text-muted-foreground hover:text-primary hover:bg-background transition-colors"
            title="Rename this QA list"
            aria-label={`Rename ${inspection.inspection_name}`}
          >
            <Pencil className="h-3.5 w-3.5" aria-hidden />
          </button>
          {onDuplicate ? (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation()
                onDuplicate(inspection)
              }}
              className="rounded-md p-1 text-muted-foreground hover:text-primary hover:bg-background transition-colors"
              title="Duplicate this QA list"
              aria-label={`Duplicate ${inspection.inspection_name}`}
            >
              <Copy className="h-3.5 w-3.5" aria-hidden />
            </button>
          ) : null}
          <button
            type="button"
            onClick={onRequestDelete}
            className="rounded-md p-1 text-muted-foreground hover:text-destructive hover:bg-background transition-colors"
            title="Delete this QA list"
            aria-label={`Delete ${inspection.inspection_name}`}
          >
            <Trash2 className="h-3.5 w-3.5" aria-hidden />
          </button>
        </div>
      )}
    </div>
  )
}
